/* ==========================================================================
   PBL cart size switcher for UpCart - Sep 2026
   Loaded from snippets/meta-tags.liquid.

   Pure Bio Labs sells each vial size as its own product (5MG, 10MG, 50MG are
   separate listings, grouped on the storefront by the SA Variants app). UpCart
   can only switch real variants of one product, so it shows nothing. This
   script adds size chips to every vial line item inside the UpCart drawer and
   swaps the line for the sibling product when a chip is tapped.

   How it works
   1. Group map: fetched once from /products.json (public storefront endpoint,
      cached in sessionStorage for an hour), filtered to product_type Vials.
      Products are grouped by title with the "(NMG)" suffix removed, so "GLP-3 VIAL (10MG)"
      and "GLP-3 VIAL (20MG)" land in one group. Nothing from SA Variants is
      read; the naming convention is the source of truth.
   2. Rendering: on every UpCart render (upcartSubscribe* hooks plus a
      MutationObserver on #UpcartPopup) each .upcart-public-cart-item is matched
      to a group by the product handle in its title link, and a chip row is
      placed at the start of the controls row, beside the quantity stepper
      (which is restyled to match). Chips are re-used if already present.
   3. Switching: the cart is rebuilt in one /cart/clear.js + /cart/add.js call
      with the swapped line in the same slot (same quantity, same customer
      properties), then upcartRefreshCart(). A plain add + remove would push the
      line to the top of the drawer because Shopify puts new lines first.

   Retire: delete this file and the script tag in snippets/meta-tags.liquid.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__pblCartSizes) return;   // loaded once, whether from the theme or UpCart Scripts
  window.__pblCartSizes = true;

  // Kevin (Sep 2026): size swatches OFF in the drawer for now. Everything else
  // (rewards bar hidden, Shipping Protection last, stepper restyle, whole-dollar
  // prices, Discount tag on the price line, image height) stays on. The full
  // version with swatches is saved as assets/pbl-cart-sizes-with-swatches.js.
  var SHOW_SWATCHES = false;

  var PRODUCTS_JSON = '/products.json?limit=250&page=';  // the vials collection is missing the newer sizes, so read the whole catalog
  var MAX_PAGES = 4;
  var CACHE_KEY = 'pbl-cart-sizes-v1';
  var CACHE_TTL = 60 * 60 * 1000;
  var SIZE_RE = /^(.*?)\s*\((\d+(?:\.\d+)?)\s*(MG|ML|MCG|IU)\)\s*$/i;

  var groups = null;      // baseTitle -> [{handle, title, size, mg, variantId, available, price}]
  var byHandle = null;    // handle -> group entry
  var loading = null;
  var busy = false;

  /* ---------------------------------------------------------------------- */
  /* Group map                                                               */
  /* ---------------------------------------------------------------------- */
  function buildGroups(products) {
    groups = {};
    byHandle = {};
    products.forEach(function (p) {
      var m = SIZE_RE.exec(p.title || '');
      if (!m || !p.variants || !p.variants.length) return;
      var base = m[1].trim().toUpperCase();
      var v = p.variants[0];
      var entry = {
        handle: p.handle,
        title: p.title,
        base: base,
        size: (m[2] + m[3]).toUpperCase(),
        mg: parseFloat(m[2]),
        unit: m[3].toUpperCase(),
        variantId: v.id,
        available: !!v.available,
        price: v.price
      };
      (groups[base] = groups[base] || []).push(entry);
      byHandle[p.handle] = entry;
    });
    Object.keys(groups).forEach(function (k) {
      groups[k].sort(function (a, b) { return a.mg - b.mg; });
      if (groups[k].length < 2) {
        groups[k].forEach(function (e) { delete byHandle[e.handle]; });
        delete groups[k];
      }
    });
  }

  function loadGroups() {
    if (!SHOW_SWATCHES) { groups = groups || {}; byHandle = byHandle || {}; return Promise.resolve(groups); }
    if (groups) return Promise.resolve(groups);
    if (loading) return loading;

    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.t && Date.now() - cached.t < CACHE_TTL && cached.products) {
        buildGroups(cached.products);
        return Promise.resolve(groups);
      }
    } catch (e) { /* noop */ }

    function fetchPage(page, acc) {
      return fetch(PRODUCTS_JSON + page, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          var list = json.products || [];
          acc = acc.concat(list);
          if (list.length === 250 && page < MAX_PAGES) return fetchPage(page + 1, acc);
          return acc;
        });
    }

    loading = fetchPage(1, [])
      .then(function (all) {
        var products = all.filter(function (p) {
          return (p.product_type || '').toLowerCase() === 'vials';
        }).map(function (p) {
          return {
            handle: p.handle,
            title: p.title,
            variants: (p.variants || []).slice(0, 1).map(function (v) {
              return { id: v.id, available: v.available, price: v.price };
            })
          };
        });
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), products: products })); } catch (e) { /* noop */ }
        buildGroups(products);
        return groups;
      })
      .catch(function (err) {
        console.warn('[pbl-cart-sizes] could not load vial groups', err);
        groups = {};
        byHandle = {};
        return groups;
      });
    return loading;
  }

  /* ---------------------------------------------------------------------- */
  /* Styles (scoped to the UpCart drawer)                                    */
  /* ---------------------------------------------------------------------- */
  function injectStyles() {
    if (document.getElementById('pbl-cart-sizes-css')) return;
    var css = [
      '#UpcartPopup .pbl-cs{display:flex;flex-wrap:wrap;gap:4px;margin:6px 0 2px;}',
      '#UpcartPopup .pbl-cs__chip{appearance:none;-webkit-appearance:none;display:inline-flex;align-items:center;justify-content:center;',
      '  min-width:40px;height:26px;padding:0 7px;margin:0;box-sizing:border-box;border:1px solid #074b7c;border-radius:6px;',
      '  background:#fff;color:#074b7c;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
      '  font-size:11px;font-weight:700;letter-spacing:.01em;line-height:1;text-transform:uppercase;cursor:pointer;',
      '  transition:background-color .15s ease,color .15s ease;}',
      '#UpcartPopup .pbl-cs__chip:hover{background:#ecf5fc;}',
      '#UpcartPopup .pbl-cs__chip[aria-pressed="true"]{background:#074b7c;color:#fff;cursor:default;}',
      '#UpcartPopup .pbl-cs__chip:disabled{opacity:.45;cursor:not-allowed;background:#fff;color:#074b7c;}',
      '#UpcartPopup .pbl-cs--busy .pbl-cs__chip{pointer-events:none;opacity:.6;}',
      /* Kevin (Sep 2026): Shipping Protection always sits at the bottom of the list */
      '#UpcartPopup .upcart-public-cart-items{display:flex;flex-direction:column;}',
      '#UpcartPopup .upcart-public-cart-item{order:0;}',
      '#UpcartPopup .upcart-public-cart-item.pbl-cs-last{order:999;}',
      /* Kevin (Sep 2026): hide the free-shipping rewards bar at the top of the drawer */
      '#UpcartPopup .upcart-public-rewards{display:none!important;}',
      /* chips share the controls row with the quantity stepper; stepper restyled to match the chips */
      '#UpcartPopup .upcart-public-component-product-tile__controls{display:flex;flex-wrap:wrap;align-items:center;gap:6px 8px;margin-top:6px;}',
      '#UpcartPopup .upcart-public-component-product-tile__controls .pbl-cs{margin:0;flex:0 1 auto;}',
      '#UpcartPopup .upcart-public-component-product-tile__quantity-selector{margin-left:auto;height:26px;min-height:0;border:1px solid #074b7c;border-radius:6px;overflow:hidden;box-sizing:border-box;}',
      '#UpcartPopup .upcart-public-component-product-tile__quantity-selector button{height:24px;min-height:0;width:26px;padding:0;color:#074b7c;}',
      '#UpcartPopup .upcart-public-component-product-tile__quantity-selector button svg{width:12px;height:12px;flex:0 0 12px;}',
      '#UpcartPopup .upcart-public-component-product-tile__quantity-selector input{height:24px;min-height:0;width:32px;padding:0;font-size:13px;font-weight:700;color:#074b7c;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}',
      /* Kevin (Sep 2026): tile image runs the full height of the text block (title down to the swatch row).
         With the Discount tag on the price line every row has the same text height, so the images match. */
      '#UpcartPopup .upcart-public-component-product-tile__content{align-items:stretch;}',
      /* Kevin (Sep 2026): same navy-to-white gradient as .card__inner on the collection cards (the vial PNGs are transparent) */
      '#UpcartPopup .upcart-public-component-product-tile__image-wrapper{height:auto;align-self:stretch;background:linear-gradient(rgb(0,93,127) 1%,#fff 70%)!important;border:1px solid #e2e2e2;border-radius:8px;overflow:hidden;box-sizing:border-box;}',
      '#UpcartPopup .upcart-public-component-product-tile__image-wrapper img{width:100%;height:100%;object-fit:contain;}',
      /* Kevin (Sep 2026): the Discount tag is small and sits on the "(Save $XX)" line, right of the savings text */
      '#UpcartPopup .upcart-public-component-product-tile__product-pricing{display:flex;align-items:center;flex-wrap:wrap;gap:6px 6px;margin-top:2px;}',
      '#UpcartPopup .upcart-public-component-product-tile__product-pricing .upcart-public-component-product-tile__discount-codes{margin-left:0;margin-top:0;}',
      /* Kevin (Sep 2026): price on its own line; "(Save $XX)" on the line below with the Discount tag to its right */
      '#UpcartPopup .upcart-public-component-product-tile__product-pricing .upcart-public-component-product-tile__price-group{flex-basis:100%;}',
      '#UpcartPopup .upcart-public-component-product-tile__product-pricing .upcart-public-component-product-tile__savings{flex:0 0 auto;order:1;margin:0;line-height:1.1;}',
      '#UpcartPopup .upcart-public-component-product-tile__product-pricing .upcart-public-component-product-tile__discount-codes{order:2;}',
      /* Kevin (Sep 2026): the discount code badge in the drawer summary ("Discounts  DISCOUNT  - $XX") matches the line badges (line-tile rules below override the size) */
      '#UpcartPopup .upcart-public-component-tag{background:#074b7c!important;border-color:#074b7c!important;color:#fff!important;border-radius:4px!important;padding:3px 7px!important;min-height:0!important;height:20px!important;display:inline-flex!important;align-items:center;gap:4px;font-size:10px!important;font-weight:700!important;letter-spacing:.02em;text-transform:uppercase;line-height:1!important;}',
      '#UpcartPopup .upcart-public-component-tag *{color:#fff!important;font-size:10px!important;line-height:1!important;}',
      '#UpcartPopup .upcart-public-component-tag svg{width:11px!important;height:11px!important;fill:#fff;}',
      '#UpcartPopup .upcart-public-cart-summary__discount-codes{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}',
      '#UpcartPopup .upcart-public-cart-summary__total-discount{font-weight:700!important;color:#2ea818!important;}',
      '#UpcartPopup .upcart-public-component-product-tile__discount-codes,#UpcartPopup .upcart-public-component-product-tile__discount-codes *{font-size:9px!important;line-height:1!important;}',
      '#UpcartPopup .upcart-public-component-product-tile__discount-codes>*{padding:3px 6px!important;border-radius:4px!important;min-height:0!important;height:18px!important;display:inline-flex!important;align-items:center;gap:3px;}',
      '#UpcartPopup .upcart-public-component-product-tile__discount-codes svg{width:10px!important;height:10px!important;fill:#fff;}',
      /* Kevin (Sep 2026): Discount badge is navy with white text */
      '#UpcartPopup .upcart-public-component-product-tile__discount-codes>*{background:#074b7c!important;border-color:#074b7c!important;color:#fff!important;}',
      '#UpcartPopup .upcart-public-component-product-tile__discount-codes *{color:#fff!important;}',
      /* Kevin (Sep 2026): product title in the system font, same as the price block, with a touch of room below it */
      '#UpcartPopup .upcart-public-component-product-tile__product-title,#UpcartPopup .upcart-public-component-product-tile__title-link{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;}',
      '#UpcartPopup .upcart-public-component-product-tile__product-title{padding-bottom:3px;}',
      /* Kevin (Sep 2026): price styled like the product card: bold navy current price, grey strikethrough original beside it */
      '#UpcartPopup .upcart-public-component-product-tile__price-group{display:flex;align-items:baseline;gap:6px;}',
      '#UpcartPopup .upcart-public-component-product-tile__price{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:14px!important;font-weight:700!important;line-height:1!important;color:#0c2d4d!important;}',
      '#UpcartPopup .upcart-public-component-product-tile__price{order:1;}',
      /* strikethrough and "(Save $XX)" share one size, clearly smaller than the sale price */
      '#UpcartPopup .pbl-compare,#UpcartPopup .upcart-public-component-product-tile__price-compare-at{order:2;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:11px!important;font-weight:500!important;line-height:1!important;color:#9aa3ad!important;text-decoration:line-through!important;}',
      '#UpcartPopup .upcart-public-component-product-tile__savings{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:11px!important;font-weight:700!important;line-height:1.1!important;text-transform:uppercase;}',
      /* Kevin (Sep 2026): quantity stepper sits on the "(Save $XX)" line, right-aligned */
      '#UpcartPopup .upcart-public-component-product-tile__product-pricing .upcart-public-component-product-tile__quantity-selector{order:3;flex:0 0 auto;margin-left:auto;}',
      '#UpcartPopup .upcart-public-component-product-tile__controls:empty{display:none;}',
      /* Kevin (Sep 2026): tighter product rows (UpCart default is 28px top and bottom) */
      '#UpcartPopup .upcart-public-component-product-tile{padding-top:16px!important;padding-bottom:16px!important;}',
      /* Kevin (Sep 2026): "Pair With" block: separator line above, tight heading, carousel card inset so it clears the arrows */
      '#UpcartPopup .upcart-public-upsell{border-top:1px solid #e6eaee!important;padding-top:14px!important;padding-bottom:4px!important;margin:4px 0 0!important;}',
      '#UpcartPopup .upcart-public-upsell__title{margin-bottom:4px!important;padding:0 30px!important;}',
      '#UpcartPopup .upcart-public-component-carousel__container{padding:0 14px!important;box-sizing:border-box;}',
      '#UpcartPopup .upcart-public-component-carousel__slide{padding:2px 10px!important;box-sizing:border-box;}',
      '#UpcartPopup .upcart-public-component-carousel__button{color:#074b7c!important;}',
      '#UpcartPopup .upcart-public-upsell__title p{line-height:1.2!important;margin:0!important;}',
      '#UpcartPopup .upcart-public-upsell__vertical-layout-wrapper{padding-top:6px!important;}',
      /* Kevin (Sep 2026): Checkout button = the card ADD TO CART (.pbl-card__atc in assets/pbl-card.css): same gradient, hover lift, one-shot shimmer */
      '#UpcartPopup .upcart-checkout-button{position:relative;overflow:hidden;isolation:isolate;background:linear-gradient(23deg,rgb(7,75,124),rgb(50,128,162) 45%,rgb(7,75,124) 98%)!important;color:#fff!important;border:0!important;border-radius:12px!important;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:15px!important;font-weight:700!important;text-transform:uppercase!important;letter-spacing:.06em!important;box-shadow:0 2px 6px rgba(7,75,124,.25)!important;transition:box-shadow .2s ease,filter .2s ease;-webkit-tap-highlight-color:transparent;}',
      '#UpcartPopup .upcart-checkout-button *{color:#fff!important;position:relative;z-index:1;}',
      '#UpcartPopup .upcart-checkout-button:hover{filter:brightness(1.06);box-shadow:0 6px 16px rgba(7,75,124,.32)!important;}',
      '#UpcartPopup .upcart-checkout-button::after{content:"";position:absolute;top:0;left:0;width:45%;height:100%;background:linear-gradient(105deg,rgba(255,255,255,0) 0%,rgba(255,255,255,.28) 50%,rgba(255,255,255,0) 100%);transform:translateX(-140%) skewX(-15deg);pointer-events:none;z-index:-1;}',
      '#UpcartPopup .upcart-checkout-button:hover::after{animation:pbl-shimmer .9s ease-out 1;}',
      '@keyframes pbl-shimmer{from{transform:translateX(-140%) skewX(-15deg);}to{transform:translateX(320%) skewX(-15deg);}}',
      '@media (prefers-reduced-motion: reduce){#UpcartPopup .upcart-checkout-button::after{display:none;}#UpcartPopup .upcart-checkout-button{transition:none;}}',
      /* Kevin (Sep 2026): compact compliance box (UpCart "Above Checkout" custom HTML, #pbl-compliance-fields): label + select on one row, tighter checkboxes. ~170px -> ~100px */
      '#UpcartPopup #pbl-compliance-fields{margin:8px 0 16px!important;padding:10px 12px!important;border:1px solid #e2e2e2!important;border-radius:8px!important;background:#f8fafc!important;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:12px!important;line-height:1.3!important;}',
      '#UpcartPopup #industry-field{display:flex!important;align-items:center!important;gap:10px!important;margin:0 0 8px!important;flex-wrap:wrap;}',
      '#UpcartPopup #industry-field label{flex:0 0 auto!important;margin:0!important;font-size:12px!important;font-weight:700!important;color:#074b7c!important;text-transform:uppercase;letter-spacing:.03em;display:inline!important;}',
      '#UpcartPopup #industry-field select{flex:1 1 160px!important;width:auto!important;min-height:0!important;height:34px!important;padding:0 10px!important;margin:0!important;border:1px solid #c9d3dc!important;border-radius:6px!important;font-size:13px!important;font-family:inherit!important;color:#0c2d4d!important;background:#fff!important;}',
      '#UpcartPopup #industry-error{flex-basis:100%!important;margin:2px 0 0!important;font-size:11px!important;}',
      '#UpcartPopup #pbl-compliance-fields>label{display:flex!important;align-items:center!important;gap:7px!important;margin:0 0 5px!important;font-size:12px!important;line-height:1.3!important;color:#1f2d3a;}',
      '#UpcartPopup #pbl-compliance-fields>label:last-of-type{margin-bottom:0!important;}',
      '#UpcartPopup #pbl-compliance-fields input[type="checkbox"]{width:15px!important;height:15px!important;min-width:15px!important;flex:0 0 15px!important;margin:0!important;accent-color:#074b7c;}',
      '#UpcartPopup #pbl-compliance-fields span[style*="color: red"],#UpcartPopup #pbl-compliance-fields span[style*="color:red"]{color:#c8102e!important;font-size:11px!important;}',
      '#UpcartPopup #pbl-compliance-fields a{color:#074b7c!important;font-weight:600!important;}',
      '#UpcartPopup #compliance-error{margin-top:6px!important;font-size:11px!important;}',
      /* Kevin (Sep 2026): "Pair With" upsell tile matches the product rows: navy caps label, white card, gradient image well, flat navy ADD+ */
      '#UpcartPopup .upcart-public-upsell__title p{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:12px!important;font-weight:700!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#074b7c!important;text-align:left!important;}',
      '#UpcartPopup .upcart-public-upsell__title{margin-bottom:6px!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile{background:#fff!important;border:1px solid #e2e2e2!important;border-radius:10px!important;padding:10px!important;gap:12px!important;align-items:stretch!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__image-container{width:88px!important;height:88px!important;background:linear-gradient(rgb(0,93,127) 1%,#fff 70%)!important;border:1px solid #e2e2e2!important;border-radius:8px!important;overflow:hidden!important;display:flex;align-items:center;justify-content:center;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__image{width:100%!important;height:100%!important;object-fit:contain!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__info{display:flex!important;flex-direction:column!important;justify-content:center!important;gap:3px!important;min-width:0;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__title{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:14px!important;font-weight:600!important;line-height:1.2!important;color:#0c2d4d!important;margin:0!important;white-space:normal!important;text-overflow:clip!important;display:-webkit-box!important;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__title-link{white-space:normal!important;overflow:visible!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__rating-row{display:flex!important;align-items:center!important;gap:5px!important;margin:0!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__stars{font-size:11px!important;color:#f5a623!important;letter-spacing:1px;line-height:1!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__reviews{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:11px!important;color:#6b7785!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__price-row{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:8px!important;margin:2px 0 0!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__price{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:14px!important;font-weight:700!important;color:#0c2d4d!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile__variant-row{margin:4px 0 0!important;}',
      '#UpcartPopup .upcart-public-component-upsell-tile .upcart-public-component__button{width:100%!important;min-height:0!important;height:32px!important;padding:0 14px!important;background:#074b7c!important;color:#fff!important;border:0!important;border-radius:6px!important;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;font-size:12px!important;font-weight:700!important;letter-spacing:.06em!important;text-transform:uppercase!important;box-shadow:0 1px 4px rgba(6,44,61,.2)!important;}',
      /* every row the same height so the images match */
      '#UpcartPopup .upcart-public-component-product-tile__info{display:flex;flex-direction:column;min-height:88px;}',
      /* phones: three chips + stepper have to fit one ~246px row */
      '@media (max-width: 600px){',
      '  #UpcartPopup .upcart-public-component-product-tile__controls{gap:6px 6px;}',
      '  #UpcartPopup .pbl-cs{gap:3px;}',
      '  #UpcartPopup .pbl-cs__chip{min-width:34px;padding:0 5px;font-size:10px;letter-spacing:0;}',
      '  #UpcartPopup .upcart-public-component-product-tile__quantity-selector button{width:22px;}',
      '  #UpcartPopup .upcart-public-component-product-tile__quantity-selector input{width:26px;font-size:12px;}',
      '}',
      /* small phones (375px): the info column is only ~178px wide, so shrink the tile image a touch to make room */
      '@media (max-width: 480px){',
      '  #UpcartPopup .upcart-public-component-product-tile__content{gap:12px;}',
      '  #UpcartPopup .upcart-public-component-product-tile__image-wrapper{width:64px;min-width:64px;}',
      '  #UpcartPopup .upcart-public-component-product-tile__quantity-selector button{width:20px;}',
      '  #UpcartPopup .upcart-public-component-product-tile__quantity-selector input{width:24px;}',
      '}'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'pbl-cart-sizes-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ---------------------------------------------------------------------- */
  /* Rendering                                                               */
  /* ---------------------------------------------------------------------- */
  function handleFromItem(item) {
    var link = item.querySelector('.upcart-public-component-product-tile__title-link[href], .upcart-public-component-product-tile__image-wrapper[href]');
    if (!link) return null;
    var href = link.getAttribute('href') || '';
    var m = /\/products\/([^/?#]+)/.exec(href);
    return m ? m[1] : null;
  }

  function quantityFromItem(item) {
    var input = item.querySelector('input[type="number"], input[inputmode="numeric"], .upcart-public-component-product-tile__quantity-input, [class*="quantity-input"]');
    var q = input ? parseInt(input.value, 10) : NaN;
    if (!q || q < 1) {
      var txt = item.querySelector('[class*="quantity-selector"]');
      var n = txt ? parseInt((txt.textContent || '').replace(/[^\d]/g, ''), 10) : NaN;
      q = n && n > 0 ? n : 1;
    }
    return q;
  }

  function renderChips(item, entry) {
    var group = groups[entry.base];
    if (!group) return;

    var row = item.querySelector('.pbl-cs');
    if (row && row.dataset.handle === entry.handle) {
      // UpCart re-renders the controls on quantity changes; make sure the row is still in them
      var ctl = item.querySelector('.upcart-public-component-product-tile__controls');
      if (ctl && row.parentNode !== ctl) ctl.insertBefore(row, ctl.firstChild);
      return;
    }
    if (row) row.remove();

    row = document.createElement('div');
    row.className = 'pbl-cs';
    row.dataset.handle = entry.handle;
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', 'Size');

    group.forEach(function (sib) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pbl-cs__chip';
      chip.textContent = sib.size;
      chip.dataset.handle = sib.handle;
      chip.dataset.variantId = sib.variantId;
      chip.setAttribute('aria-pressed', sib.handle === entry.handle ? 'true' : 'false');
      chip.title = sib.title;
      if (!sib.available) {
        chip.disabled = true;
        chip.title = sib.title + ' (sold out)';
      }
      chip.addEventListener('click', function () {
        if (sib.handle === entry.handle) return;
        switchLine(item, entry, sib);
      });
      row.appendChild(chip);
    });

    // Chips go at the start of the controls row so they sit beside the quantity stepper
    var controls = item.querySelector('.upcart-public-component-product-tile__controls');
    var anchor = item.querySelector('.upcart-public-component-product-tile__product-pricing')
      || item.querySelector('.upcart-public-component-product-tile__variant')
      || item.querySelector('.upcart-public-component-product-tile__header');
    if (controls) {
      controls.insertBefore(row, controls.firstChild);
    } else if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(row, anchor.nextSibling);
    } else {
      var info = item.querySelector('.upcart-public-component-product-tile__info');
      if (info) info.appendChild(row);
    }
  }

  // Kevin (Sep 2026): line prices show whole dollars ($140.25 -> $140, Save $24.72 -> Save $25).
  // Only the per-line price block is touched; subtotal / checkout keep the exact amounts.
  var MONEY_RE = /\$(\d{1,3}(?:,\d{3})*|\d+)\.(\d{2})\b/g;
  // Keep UpCart's exact source text separate from our rounded display. A new
  // text node starts fresh; an UpCart write to an existing node replaces its
  // source, even when the new value happens to equal our rounded display.
  var moneyText = new WeakMap();
  var moneyWrites = new WeakMap();
  function sourceMoneyText(node) {
    var current = node.nodeValue || '';
    var state = moneyText.get(node);
    if (!state || current !== state.display) {
      state = { source: current, display: current };
      moneyText.set(node, state);
    }
    return state.source;
  }
  function sourceMoneyContent(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var node, text = '';
    while ((node = walker.nextNode())) text += sourceMoneyText(node);
    return text;
  }
  function rememberMoneyMutations(mutations) {
    var nextValues = new WeakMap();
    var values = [];
    var ownWrites = new WeakSet();
    // Mutation records carry the old value. For several writes in one batch,
    // the following record's old value is the preceding write's new value.
    for (var i = mutations.length - 1; i >= 0; i--) {
      var mutation = mutations[i];
      if (mutation.type !== 'characterData') continue;
      values[i] = nextValues.has(mutation.target) ? nextValues.get(mutation.target) : mutation.target.nodeValue;
      nextValues.set(mutation.target, mutation.oldValue);
    }
    for (var j = 0; j < mutations.length; j++) {
      var change = mutations[j];
      if (change.type !== 'characterData') continue;
      var writes = moneyWrites.get(change.target);
      var write = writes && writes[0];
      if (write && write.before === change.oldValue && write.after === values[j]) {
        writes.shift();
        if (!writes.length) moneyWrites.delete(change.target);
        var state = moneyText.get(change.target);
        if (state) state.display = values[j];
        ownWrites.add(change);
      } else {
        moneyText.set(change.target, { source: values[j], display: values[j] });
      }
    }
    return ownWrites;
  }
  function roundMoneyText(text) {
    return text.replace(MONEY_RE, function (m, whole, cents) {
      var n = Math.round(parseFloat(whole.replace(/,/g, '') + '.' + cents));
      return '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    });
  }
  function parseMoney(text) {
    var m = /\$(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d{2}))?/.exec(text || '');
    return m ? parseFloat(m[1].replace(/,/g, '') + '.' + (m[2] || '00')) : NaN;
  }
  function formatMoney(n) {
    return '$' + String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  // Original (pre-discount) price shown struck through next to the sale price, like the product card.
  // UpCart only prints the discounted price and the savings, so the original is price + savings.
  function renderCompareAt(block) {
    var priceEl = block.querySelector('.upcart-public-component-product-tile__price');
    var saveEl = block.querySelector('.upcart-public-component-product-tile__savings');
    var group = block.querySelector('.upcart-public-component-product-tile__price-group');
    var existing = block.querySelector('.pbl-compare');
    // UpCart's own strikethrough (Design > "Show strikethrough prices") takes priority when it is on
    if (block.querySelector('.upcart-public-component-product-tile__price-compare-at')) { if (existing) existing.remove(); return; }
    if (!priceEl || !saveEl || !group) { if (existing) existing.remove(); return; }
    var price = parseMoney(sourceMoneyContent(priceEl));
    var save = parseMoney(sourceMoneyContent(saveEl));
    if (!(price > 0) || !(save > 0)) { if (existing) existing.remove(); return; }
    var label = formatMoney(price + save);
    if (existing) { if (existing.textContent !== label) existing.textContent = label; return; }
    var s = document.createElement('s');
    s.className = 'pbl-compare';
    s.textContent = label;
    group.appendChild(s);
  }

  function roundLinePrices(popup) {
    var blocks = popup.querySelectorAll('.upcart-public-component-product-tile__product-pricing');
    for (var i = 0; i < blocks.length; i++) {
      renderCompareAt(blocks[i]);   // uses UpCart's exact amounts on every pass
      var walker = document.createTreeWalker(blocks[i], NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var source = sourceMoneyText(node);
        var display = roundMoneyText(source);
        moneyText.get(node).display = display;
        if (node.nodeValue !== display) {
          var writes = moneyWrites.get(node) || [];
          writes.push({ before: node.nodeValue, after: display });
          moneyWrites.set(node, writes);
          node.nodeValue = display;
        }
      }
    }
  }

  // Kevin (Sep 2026): remember the compliance box (Industry + the two checkboxes, UpCart "Above Checkout"
  // custom HTML) so a customer who leaves checkout to keep browsing does not have to fill it in again.
  // Saved in localStorage for 30 days; restored on every drawer render. A synthetic "change" event is
  // fired after restoring so the UpCart validation script (Scripts > Before Load) re-checks the fields
  // and lifts the greyed-out Checkout state.
  var COMPLIANCE_KEY = 'pbl-compliance-v1';
  var COMPLIANCE_TTL = 30 * 24 * 60 * 60 * 1000;
  var COMPLIANCE_IDS = { 'industry-select': 'industry', 'terms-check': 'terms', 'research-purpose-check': 'research' };

  function readCompliance() {
    try {
      var saved = JSON.parse(localStorage.getItem(COMPLIANCE_KEY) || 'null');
      if (!saved || !saved.t || Date.now() - saved.t > COMPLIANCE_TTL) return null;
      return saved;
    } catch (e) { return null; }
  }

  function saveCompliance() {
    var industry = document.getElementById('industry-select');
    var terms = document.getElementById('terms-check');
    var research = document.getElementById('research-purpose-check');
    if (!industry && !terms && !research) return;
    var data = readCompliance() || {};
    if (industry) data.industry = industry.value || '';
    if (terms) data.terms = !!terms.checked;
    if (research) data.research = !!research.checked;
    data.t = Date.now();
    try { localStorage.setItem(COMPLIANCE_KEY, JSON.stringify(data)); } catch (e) { /* noop */ }
  }

  function restoreCompliance() {
    var box = document.getElementById('pbl-compliance-fields');
    if (!box || box.dataset.pblRestored) return;
    var saved = readCompliance();
    box.dataset.pblRestored = '1';
    if (!saved) return;
    var changed = [];
    var industry = document.getElementById('industry-select');
    if (industry && saved.industry && !industry.value) {
      var hasOption = Array.prototype.some.call(industry.options, function (o) { return o.value === saved.industry; });
      if (hasOption) { industry.value = saved.industry; changed.push(industry); }
    }
    var terms = document.getElementById('terms-check');
    if (terms && saved.terms && !terms.checked) { terms.checked = true; changed.push(terms); }
    var research = document.getElementById('research-purpose-check');
    if (research && saved.research && !research.checked) { research.checked = true; changed.push(research); }
    changed.forEach(function (el) {
      try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) { /* noop */ }
    });
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.id && COMPLIANCE_IDS[t.id]) saveCompliance();
  }, true);

  function decorate() {
    var popup = document.getElementById('UpcartPopup');
    if (!popup) return;
    restoreCompliance();
    roundLinePrices(popup);
    var items = popup.querySelectorAll('.upcart-public-cart-item');
    if (!items.length) return;

    loadGroups().then(function () {
      injectStyles();
      items.forEach(function (item) {
        var handle = handleFromItem(item);
        // UpCart adds Shipping Protection itself as a reward product; that tile has no product link,
        // so match on the title text as well as the handle.
        var titleEl = item.querySelector('.upcart-public-component-product-tile__product-title');
        var isShipping = handle === 'shipping-protection' || /shipping protection/i.test(titleEl ? titleEl.textContent : '');
        item.classList.toggle('pbl-cs-last', isShipping);
        // Discount tag moves from the controls row up to the price line
        var tag = item.querySelector('.upcart-public-component-product-tile__discount-codes');
        var pricing = item.querySelector('.upcart-public-component-product-tile__product-pricing');
        if (tag && pricing && tag.parentNode !== pricing) pricing.appendChild(tag);
        // Quantity stepper joins the pricing block so it lines up with "(Save $XX)"
        var stepper = item.querySelector('.upcart-public-component-product-tile__quantity-selector');
        if (!SHOW_SWATCHES && stepper && pricing && stepper.parentNode !== pricing) pricing.appendChild(stepper);
        var entry = SHOW_SWATCHES && handle && byHandle[handle];
        if (entry) renderChips(item, entry);
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Switching                                                               */
  /* ---------------------------------------------------------------------- */
  function getCart() {
    // Read the live cart straight from Shopify (cache: no-store; the browser
    // otherwise serves a stale /cart.js and the old line never gets removed).
    return fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function lineToAdd(li) {
    var out = { id: li.variant_id, quantity: li.quantity };
    if (li.properties && Object.keys(li.properties).length) out.properties = li.properties;
    if (li.selling_plan_allocation && li.selling_plan_allocation.selling_plan) out.selling_plan = li.selling_plan_allocation.selling_plan.id;
    return out;
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); });
  }

  function switchLine(item, from, to) {
    if (busy) return;
    busy = true;
    var row = item.querySelector('.pbl-cs');
    if (row) row.classList.add('pbl-cs--busy');
    var qty = quantityFromItem(item);
    var original = null;

    getCart()
      .then(function (cart) {
        var items = cart.items || [];
        original = items.map(lineToAdd);

        var idx = -1;
        items.forEach(function (li, i) {
          if (idx === -1 && (li.handle === from.handle || String(li.variant_id) === String(from.variantId))) idx = i;
        });
        var line = idx > -1 ? items[idx] : null;
        var quantity = line ? line.quantity : qty;

        // Carry over the customer's own line properties only. App-private ones
        // (__awesome_quantity_stats, __upcartUpsell, ...) are session junk.
        var properties = {};
        if (line && line.properties) {
          Object.keys(line.properties).forEach(function (k) {
            if (k.indexOf('__') !== 0) properties[k] = line.properties[k];
          });
        }

        // Build the new line list with the swapped line kept in the same slot.
        // If the target size is already in the cart, fold the quantity into that
        // line instead of creating a second one.
        var next = items.map(lineToAdd);
        var existingIdx = -1;
        items.forEach(function (li, i) {
          if (existingIdx === -1 && i !== idx && String(li.variant_id) === String(to.variantId)) existingIdx = i;
        });
        var swapped = { id: to.variantId, quantity: quantity };
        if (Object.keys(properties).length) swapped.properties = properties;
        if (line && line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) swapped.selling_plan = line.selling_plan_allocation.selling_plan.id;

        if (existingIdx > -1) {
          next[existingIdx].quantity += quantity;
          if (idx > -1) next.splice(idx, 1);
        } else if (idx > -1) {
          next[idx] = swapped;
        } else {
          next.unshift(swapped);
        }

        // Shopify puts every newly added line at the top, so the cart is rebuilt
        // in one add call with the list reversed: the last item lands on top.
        return postJson('/cart/clear.js', {}).then(function () {
          return postJson('/cart/add.js', { items: next.slice().reverse() });
        }).then(function (res) {
          if (!res.ok || res.json.status) {
            // Put the cart back the way it was before surfacing the error.
            return postJson('/cart/add.js', { items: original.slice().reverse() }).then(function () {
              throw new Error(res.json.description || res.json.message || 'add failed');
            });
          }
        });
      })
      .then(function () {
        if (typeof window.upcartRefreshCart === 'function') {
          try { window.upcartRefreshCart(); } catch (e) { /* noop */ }
        }
        setTimeout(decorate, 400);
        setTimeout(decorate, 1200);
      })
      .catch(function (err) {
        console.error('[pbl-cart-sizes] switch failed', err);
        if (typeof window.upcartRefreshCart === 'function') {
          try { window.upcartRefreshCart(); } catch (e) { /* noop */ }
        }
        if (typeof window.showUpcartErrorToast === 'function') {
          try { window.showUpcartErrorToast('Could not switch size. Please try again.'); } catch (e) { /* noop */ }
        }
      })
      .then(function () {
        busy = false;
        if (row) row.classList.remove('pbl-cs--busy');
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ---------------------------------------------------------------------- */
  var scheduled = null;
  function scheduleDecorate() {
    if (scheduled) return;
    scheduled = setTimeout(function () { scheduled = null; decorate(); }, 80);
  }

  function hook() {
    ['upcartSubscribeCartLoaded', 'upcartSubscribeCartUpdated', 'upcartSubscribeCartOpened', 'upcartSubscribeAddedToCart', 'upcartSubscribeItemRemoved'].forEach(function (name) {
      if (typeof window[name] === 'function') {
        try { window[name](scheduleDecorate); } catch (e) { /* noop */ }
      }
    });
  }

  function observe() {
    var target = document.getElementById('upCart') || document.body;
    var observer = new MutationObserver(function (mutations) {
      var ownWrites = rememberMoneyMutations(mutations);
      for (var i = 0; i < mutations.length; i++) {
        if (ownWrites.has(mutations[i])) continue;
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.id === 'UpcartPopup' || node.id === 'pbl-compliance-fields' || node.classList.contains('upcart-public-cart-item') || (node.querySelector && (node.querySelector('.upcart-public-cart-item') || node.querySelector('#pbl-compliance-fields')))) {
            scheduleDecorate();
            return;
          }
        }
        // price text re-rendered in place (quantity change, discount recalculated)
        var t = mutations[i].target;
        if (t && t.nodeType === 3) t = t.parentElement;
        if (t && t.closest && t.closest('.upcart-public-component-product-tile__product-pricing')) {
          scheduleDecorate();
          return;
        }
      }
    });
    observer.observe(target, { childList: true, subtree: true, characterData: true, characterDataOldValue: true });
  }

  function init() {
    // UpCart's bundle can land after DOMContentLoaded; poll briefly for its API.
    var tries = 0;
    (function wait() {
      if (typeof window.upcartOpenCart === 'function' || document.getElementById('UpcartPopup')) {
        hook();
        observe();
        scheduleDecorate();
        return;
      }
      if (tries++ < 50) setTimeout(wait, 200);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
